import React from 'react';
import { Languages, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'react-toastify';

const LanguageSettings = ({ websiteConfig, onLanguageUpdate, isProcessing }) => {

    // Default languages if not set in config
    const targetLanguages = websiteConfig?.targetLanguages || [
        { code: 'yo', name: 'Yoruba', flag: '🇳🇬', enabled: true },
        { code: 'sw', name: 'Swahili', flag: '🇰🇪', enabled: true },
        { code: 'pcm', name: 'Nigerian Pidgin', flag: '🇳🇬', enabled: true }
    ];

    const defaultLanguage = websiteConfig?.defaultLanguage || 'English';

    const handleToggleLanguage = (code) => {
        if (isProcessing) return;

        const updatedLanguages = targetLanguages.map(lang =>
            lang.code === code ? { ...lang, enabled: !lang.enabled } : lang
        );

        // Ensure at least one language is enabled
        const activeCount = updatedLanguages.filter(l => l.enabled).length;
        if (activeCount === 0) {
            toast.warning("You must have at least one target language enabled.");
            return;
        }

        onLanguageUpdate(defaultLanguage, updatedLanguages);
    };

    const handleDefaultLangChange = (e) => {
        if (isProcessing) return;
        onLanguageUpdate(e.target.value, targetLanguages);
    };

    if (!websiteConfig?.domain) {
        return (
            <div className="bg-white rounded-xl border border-[#E0E4E2] shadow-sm p-8 text-center text-gray-500">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Languages className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Language Settings Locked</h3>
                <p className="text-sm">Please register your website domain first to configure languages.</p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-[#E0E4E2] shadow-sm overflow-hidden mt-6">
            <div className="p-6 border-b border-[#E0E4E2] bg-[#FDFDFD]">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Languages className="w-5 h-5 text-[#1E6C56]" />
                    Language Configuration
                </h3>
                <p className="text-gray-500 text-sm mt-1">Manage the default language of your site and which languages users can translate it to.</p>
            </div>

            <div className="p-6 space-y-8">
                {/* Default Language Selector */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Website Default Language</h4>
                    <p className="text-xs text-gray-500 mb-3">The original language your website is written in.</p>
                    <div className="relative w-full max-w-sm">
                        <select
                            value={defaultLanguage}
                            onChange={handleDefaultLangChange}
                            disabled={isProcessing}
                            className="block w-full pl-4 pr-10 py-3 text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-[#FFB800] focus:border-[#FFB800] sm:text-sm font-medium disabled:opacity-50"
                        >
                            <option value="English">English 🇬🇧</option>
                            {/* In the future, allow Yoruba/Swahili as default */}
                        </select>
                    </div>
                </div>

                <hr className="border-gray-100" />

                {/* Target Languages Toggles */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Available Target Languages</h4>
                    <p className="text-xs text-gray-500 mb-4">Users will see these languages in the translation widget on your site.</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {targetLanguages.map((lang) => (
                            <div
                                key={lang.code}
                                className={`flex items-center justify-between p-4 rounded-xl border transition-all ${lang.enabled
                                        ? 'border-[#1E6C56] bg-green-50 shadow-sm'
                                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-2xl">{lang.flag}</span>
                                    <div>
                                        <p className={`font-bold text-sm ${lang.enabled ? 'text-[#1E6C56]' : 'text-gray-700'}`}>
                                            {lang.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {lang.enabled ? 'Active' : 'Disabled'}
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => handleToggleLanguage(lang.code)}
                                    disabled={isProcessing}
                                    className={`transition-colors focus:outline-none ${lang.enabled ? 'text-[#1E6C56]' : 'text-gray-300 hover:text-gray-400'
                                        } disabled:opacity-50`}
                                >
                                    {lang.enabled ? (
                                        <ToggleRight className="w-8 h-8" />
                                    ) : (
                                        <ToggleLeft className="w-8 h-8 text-gray-400" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Visual indicator of processing state */}
            {isProcessing && (
                <div className="bg-blue-50 p-3 text-center text-sm text-blue-700 border-t border-blue-100 flex items-center justify-center gap-2 font-medium">
                    <div className="w-4 h-4 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
                    Updating language settings...
                </div>
            )}
        </div>
    );
};

export default LanguageSettings;
